// « Paiements » — every payment of a period by tender, and the card ones
// matched to the terminal, for the bank reconciliation. bos.* only (operator,
// 2026-10-01): the desk has no business with the books.
//
// Source of the money is the Vega mirror (the book the bank is reconciled
// against until go-live), read through the `VegaMirror` port in one-month
// slices. Source of the card taps is a Sextant portal snapshot the operator
// loads here (`bosa fetch-sextant`): the portal needs a login the browser must
// not hold, and nothing from it is in the database yet. The snapshot is read
// in this page's memory and goes nowhere — no upload, no storage, no PAN (the
// reader refuses an unmasked one).
//
// All rules (what is money, what matches) live in paymentsByType.ts.

import { useEffect, useMemo, useState } from 'react';
import type { IsoDate, MirrorPayment } from '@lemontaigu/pms-core';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import { addDaysIso, todayIsoParis } from '@lemontaigu/staff';
import { usePms, usePmsScope } from '@lemontaigu/pms/PmsProvider.js';
import { LedgerGateBanner } from '@lemontaigu/pms/LedgerGateBanner.js';
import { PeriodNav } from './Pointages.js';
import { fmtDateShort, summaryRange, type SummaryGranularity } from './logic.js';
import {
  fmtCents,
  isConclusive,
  isEncaissement,
  matchCard,
  monthSlices,
  readSnapshots,
  summarise,
  tenderInfo,
  toCents,
  type SnapshotRead,
} from './paymentsByType.js';

/** Rows drawn before « Tout afficher » — a fiscal year is thousands. */
const ROW_LIMIT = 300;

const signed = (cents: number) => (
  <span className={cents < 0 ? 'occ-neg' : undefined}>{fmtCents(cents)}</span>
);

const clock = (at: string) => `${at.slice(8, 10)}/${at.slice(5, 7)} ${at.slice(11, 16)}`;

export function Paiements() {
  const { vegaMirror } = usePms();
  const { current } = usePmsScope();
  const canRead = useCapability(CAPABILITIES.PMS_MIRROR_READ);

  const [granularity, setGranularity] = useState<SummaryGranularity>('month');
  const [anchor, setAnchor] = useState(todayIsoParis());
  const { fromDay, toDay } = summaryRange(granularity, anchor, true);

  const [payments, setPayments] = useState<MirrorPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tender, setTender] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [snapshot, setSnapshot] = useState<SnapshotRead | null>(null);
  const [snapshotNames, setSnapshotNames] = useState<string[]>([]);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);

  useEffect(() => {
    if (!current || !canRead) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setShowAll(false);
    void (async () => {
      const all: MirrorPayment[] = [];
      for (const slice of monthSlices(fromDay, toDay)) {
        const res = await vegaMirror.payments(current, slice.from, slice.to);
        if (cancelled) return;
        if (!res.ok) {
          setLoading(false);
          setPayments([]);
          setError(res.error.message ?? 'lecture impossible');
          return;
        }
        all.push(...res.value);
      }
      setPayments(all);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [vegaMirror, current, canRead, fromDay, toDay]);

  const summary = useMemo(() => summarise(payments), [payments]);

  // The card movements, in a stable order, so a match can point at an index.
  const cardMovements = useMemo(
    () =>
      payments
        .filter((p) => isEncaissement(p) && tenderInfo(p.method as string).card)
        .sort((a, b) => (a.movementDate ?? '').localeCompare(b.movementDate ?? '')),
    [payments],
  );

  const match = useMemo(
    () =>
      snapshot
        ? matchCard(
            cardMovements.map((p) => ({
              date: p.movementDate ?? '',
              cents: toCents(p.settledAmount as number),
              method: p.method as string,
            })),
            snapshot.taps,
          )
        : null,
    [snapshot, cardMovements],
  );
  const matchByMovement = useMemo(() => new Map(match?.matches.map((m) => [m.movement, m]) ?? []), [match]);
  const movementIndex = useMemo(() => new Map(cardMovements.map((p, i) => [p, i])), [cardMovements]);

  const vegaPeriod = { from: fromDay, to: toDay };
  const unmatchedMovements = (match?.unmatchedMovements ?? []).map((i) => ({
    p: cardMovements[i]!,
    conclusive: isConclusive(cardMovements[i]!.movementDate ?? '', match?.coverage ?? null),
  }));
  // A tap with no Vega partner is a finding only inside the period read (its
  // partner would be dated inside it) and with its whole window covered.
  const unmatchedTaps = (match?.unmatchedTaps ?? [])
    .filter((t) => t.at.slice(0, 10) >= fromDay && t.at.slice(0, 10) <= toDay)
    .map((t) => ({ t, conclusive: isConclusive(t.at.slice(0, 10), vegaPeriod) }));
  const remisesInPeriod = (snapshot?.remises ?? []).filter(
    (r) => r.settledAt.slice(0, 10) >= fromDay && r.settledAt.slice(0, 10) <= toDay,
  );

  const loadFiles = async (files: FileList | null) => {
    setSnapshotError(null);
    if (!files || files.length === 0) return;
    try {
      const parsed = await Promise.all([...files].map(async (f) => JSON.parse(await f.text()) as unknown));
      setSnapshot(readSnapshots(parsed));
      setSnapshotNames([...files].map((f) => f.name));
    } catch (e) {
      setSnapshot(null);
      setSnapshotNames([]);
      setSnapshotError(e instanceof Error ? e.message : String(e));
    }
  };

  const detail = useMemo(
    () => payments.filter((p) => isEncaissement(p) && (tender === null || p.method === tender)),
    [payments, tender],
  );
  const shown = showAll ? detail : detail.slice(0, ROW_LIMIT);
  const tenderIsCard = tender !== null && tenderInfo(tender).card;

  if (!canRead) {
    return (
      <div className="occ">
        <p className="muted">Cette page demande l’accès au Miroir Vega (pms.mirror.read).</p>
      </div>
    );
  }

  return (
    <div className="occ pay">
      <header className="occ-head">
        <div>
          <h1>Paiements par mode</h1>
          <p className="muted">
            Encaissements de Vega (arrhes versées + règlements) sur la période, par mode de paiement —
            l’année est l’exercice du 1<sup>er</sup> décembre au 30 novembre. Les arrhes <em>déduites</em> ne
            comptent pas : c’est un mouvement interne, sans ligne en banque.
          </p>
        </div>
      </header>

      <PeriodNav
        granularity={granularity}
        anchor={anchor}
        onGranularity={setGranularity}
        onAnchor={setAnchor}
        fiscalYear
      />

      <LedgerGateBanner day={addDaysIso(todayIsoParis(), -1) as IsoDate} />

      {error && <p className="occ-error">{error}</p>}
      {loading && <p className="muted">Lecture de Vega…</p>}

      {!loading && !error && (
        <>
          <table className="occ-table pay-summary">
            <thead>
              <tr>
                <th>Mode</th>
                <th className="occ-amount">Nb</th>
                <th className="occ-amount">Arrhes</th>
                <th className="occ-amount">Règlements</th>
                <th className="occ-amount">Total</th>
              </tr>
            </thead>
            <tbody>
              {summary.rows.map((r) => (
                <tr
                  key={r.code}
                  className={`pay-pick${tender === r.code ? ' is-active' : ''}`}
                  onClick={() => setTender(tender === r.code ? null : r.code)}
                  title="Afficher le détail de ce mode"
                >
                  <th scope="row">
                    {r.label} <span className="muted">· {r.code}</span>
                  </th>
                  <td className="occ-amount">{r.count}</td>
                  <td className="occ-amount">{signed(r.arrhesCents)}</td>
                  <td className="occ-amount">{signed(r.reglCents)}</td>
                  <td className="occ-amount">
                    <strong>{signed(r.totalCents)}</strong>
                  </td>
                </tr>
              ))}
              {summary.rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    Aucun encaissement sur cette période.
                  </td>
                </tr>
              )}
            </tbody>
            {summary.rows.length > 0 && (
              <tfoot>
                <tr>
                  <th scope="row">Total encaissé</th>
                  <td className="occ-amount">{summary.count}</td>
                  <td />
                  <td />
                  <td className="occ-amount">
                    <strong>{signed(summary.totalCents)}</strong>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>

          {summary.others.length > 0 && (
            <p className="muted pay-others">
              Hors encaissement (non totalisé) :{' '}
              {summary.others.map((o, i) => (
                <span key={o.kind}>
                  {i > 0 && ' · '}
                  {o.label} {o.kind} — {o.count} mvt, {fmtCents(o.cents)}
                </span>
              ))}
            </p>
          )}

          <section className="pay-card">
            <h2>Cartes ↔ terminal</h2>
            <p className="muted">
              Même montant, même contrat, à ±2 jours, chaque débit du terminal utilisé une seule fois. Charger
              l’export du portail Sextant
              {' '}(<code>bosa fetch-sextant --period AAAA-MM</code> → <code>sextant-portal-AAAA-MM.json</code>) ;
              plusieurs mois à la fois si la période en couvre plusieurs. Le fichier reste dans cette page : rien
              n’est envoyé ni conservé.
            </p>
            <input type="file" accept=".json,application/json" multiple onChange={(e) => void loadFiles(e.target.files)} />
            {snapshotError && <p className="occ-error">{snapshotError}</p>}

            {snapshot && match && (
              <>
                <p>
                  <strong>{match.matches.length}</strong> mouvement(s) carte rapproché(s) sur{' '}
                  <strong>{cardMovements.length}</strong> ·{' '}
                  <span className="muted">
                    fichiers : {snapshotNames.join(', ')} — débits du {match.coverage ? fmtDateShort(match.coverage.from) : '—'} au{' '}
                    {match.coverage ? fmtDateShort(match.coverage.to) : '—'}
                  </span>
                </p>
                {snapshot.warnings.map((w) => (
                  <p key={w} className="pay-warn">⚠ {w}</p>
                ))}

                <h3>Remises de la période</h3>
                <table className="occ-table">
                  <thead>
                    <tr>
                      <th>Remise</th>
                      <th>Télécollecte</th>
                      <th>Contrat</th>
                      <th className="occ-amount">Opérations</th>
                      <th className="occ-amount">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {remisesInPeriod.map((r) => (
                      <tr key={`${r.number}-${r.contract}`}>
                        <th scope="row">{r.number}</th>
                        <td>{clock(r.settledAt)}</td>
                        <td className="muted">{r.label || r.contract}</td>
                        <td className="occ-amount">{r.taps}</td>
                        <td className="occ-amount">{signed(r.cents)}</td>
                      </tr>
                    ))}
                    {remisesInPeriod.length === 0 && (
                      <tr>
                        <td colSpan={5} className="muted">
                          Aucune remise télécollectée dans la période.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <h3>Vega sans débit au terminal ({unmatchedMovements.length})</h3>
                <p className="muted">
                  À examiner : un mode mal saisi (espèces ou ANCV passés en carte), une saisie en double, ou un
                  débit sur un autre terminal. « Fichier incomplet » = la fenêtre de ±2 jours dépasse les fichiers
                  chargés, ce n’est pas encore un écart.
                </p>
                <table className="occ-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Mouvement</th>
                      <th>Réf.</th>
                      <th>Payeur</th>
                      <th>Mode</th>
                      <th className="occ-amount">Réglé</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {unmatchedMovements.map(({ p, conclusive }, i) => (
                      <tr key={i} className={conclusive ? 'pay-ko' : undefined}>
                        <th scope="row">{p.movementDate ? fmtDateShort(p.movementDate) : '—'}</th>
                        <td className="muted">{p.kind}</td>
                        <td className="muted">{p.reference ?? '—'}</td>
                        <td>{p.name ?? '—'}</td>
                        <td className="muted">{p.method}</td>
                        <td className="occ-amount">{signed(toCents(p.settledAmount as number))}</td>
                        <td className="muted">{conclusive ? '' : 'Fichier incomplet'}</td>
                      </tr>
                    ))}
                    {unmatchedMovements.length === 0 && (
                      <tr>
                        <td colSpan={7} className="muted">Tout est rapproché.</td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <h3>Terminal sans mouvement Vega ({unmatchedTaps.length})</h3>
                <p className="muted">
                  Un débit réel que le desk n’a pas saisi dans Vega — ou saisi à plus de 2 jours, ou avec un autre
                  montant.
                </p>
                <table className="occ-table">
                  <thead>
                    <tr>
                      <th>Débit</th>
                      <th>Carte</th>
                      <th>Autorisation</th>
                      <th>Remise</th>
                      <th className="occ-amount">Montant</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {unmatchedTaps.map(({ t, conclusive }) => (
                      <tr key={t.id} className={conclusive ? 'pay-ko' : undefined}>
                        <th scope="row">{clock(t.at)}</th>
                        <td className="muted">…{t.last4} {t.application}</td>
                        <td className="muted">{t.authorisation ?? '—'}</td>
                        <td className="muted">{t.remise}</td>
                        <td className="occ-amount">{signed(t.cents)}</td>
                        <td className="muted">{conclusive ? '' : 'Bord de période'}</td>
                      </tr>
                    ))}
                    {unmatchedTaps.length === 0 && (
                      <tr>
                        <td colSpan={6} className="muted">Aucun.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </>
            )}
          </section>

          <section className="pay-detail">
            <h2>
              Détail — {tender === null ? 'tous les modes' : tenderInfo(tender).label}{' '}
              <span className="muted">({detail.length})</span>
              {tender !== null && (
                <button type="button" className="pay-clear" onClick={() => setTender(null)}>
                  Tous les modes
                </button>
              )}
            </h2>
            <table className="occ-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Mouvement</th>
                  <th>Réf.</th>
                  <th>Payeur</th>
                  <th>Mode</th>
                  <th className="occ-amount">Réglé</th>
                  {(tenderIsCard || tender === null) && snapshot && <th>Terminal</th>}
                </tr>
              </thead>
              <tbody>
                {shown.map((p, i) => {
                  const idx = movementIndex.get(p);
                  const m = idx === undefined ? undefined : matchByMovement.get(idx);
                  const isCard = idx !== undefined;
                  return (
                    <tr key={`${p.reference ?? 'x'}-${p.movementDate ?? ''}-${i}`}>
                      <th scope="row">{p.movementDate ? fmtDateShort(p.movementDate) : '—'}</th>
                      <td className="muted">{p.kind}</td>
                      <td className="muted">{p.reference ?? '—'}</td>
                      <td>{p.name ?? '—'}</td>
                      <td className="muted">{tenderInfo(p.method as string).label}</td>
                      <td className="occ-amount">{signed(toCents(p.settledAmount as number))}</td>
                      {(tenderIsCard || tender === null) && snapshot && (
                        <td className={m ? 'pay-ok' : isCard ? 'pay-ko' : 'muted'}>
                          {m
                            ? `✓ ${clock(m.tap.at)} …${m.tap.last4} · remise ${m.tap.remise}`
                            : isCard
                              ? '✗ sans débit terminal'
                              : ''}
                        </td>
                      )}
                    </tr>
                  );
                })}
                {detail.length === 0 && (
                  <tr>
                    <td colSpan={7} className="muted">Aucun encaissement.</td>
                  </tr>
                )}
              </tbody>
            </table>
            {!showAll && detail.length > ROW_LIMIT && (
              <p>
                <button type="button" onClick={() => setShowAll(true)}>
                  Tout afficher ({detail.length})
                </button>
              </p>
            )}
          </section>
        </>
      )}
    </div>
  );
}
